import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import PsychologyIcon from "@mui/icons-material/Psychology";
import StorageIcon from "@mui/icons-material/Storage";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { AxiosService } from "../../../services/axios/axiosService";

const CandidateDisclaimer = () => {
  const [isChecked, setIsChecked] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams();
  const { interviewId } = useLocation().state || {};
  const [loading, setLoading] = useState(false);

  const handleProceed = async () => {
    setLoading(true);
    try {
      await AxiosService.post(`/api/Interview/${interviewId}/accept-consent`);
      navigate(`/device-check/${token}`, { state: { interviewId: interviewId } });
    } catch (err) {
      console.error(err);
      toast.error(
        err.message || "Oops! Something went wrong while retrieving the data.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        maxWidth: "md",
        mx: "auto",
        mt: 2,
        mb: 2,
        border: "1px solid #E0E0E0",
        boxShadow: "none",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header Section */}
        <Box sx={{ px: 5, pt: 3,  }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Disclaimer & Consent
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please review and accept before proceeding with your interview.
          </Typography>
        </Box>

        {/* Divider below title */}
        <Divider sx={{ borderColor: "#E0E0E0" }} />

        {/* Content Section */}
        <Box sx={{ px: 5, py: 3 }}>
          <Stack spacing={3}>
            {/* AI Monitoring Section */}
            <Box
              sx={{
                backgroundColor: "#EBF5FF",
                borderRadius: "8px",
                borderLeft: "6px solid #2196F3", // Thicker blue left border
                p: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <PsychologyIcon sx={{ color: "#2196F3", fontSize: 28 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    IGNA Scout Monitoring
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    This interview is monitored by artificial intelligence to
                    ensure integrity and assess your responses. The AI driven IGNA Scout system
                    will:
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      "Monitor your behavior and engagement during the interview",
                      "Analyze your responses for relevance and quality",
                      "Track tab switching, typing activity, and face visibility",
                      "Generate automated scores for HR review",
                    ].map((text, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 20, color: "#2196F3" }}
                        />
                        <Typography variant="caption">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>

            {/* Interview Recording Section */}
            <Box
              sx={{
                backgroundColor: "#F3E5F5",
                borderRadius: "8px",
                borderLeft: "6px solid #9C27B0", // Thicker purple left border
                p: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <VideocamIcon sx={{ color: "#9C27B0", fontSize: 28 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    IGNA Scout Recording
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Your interview will be recorded (video and audio) for the
                    following purposes:
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      "Enable HR team to review your responses",
                      "Support AI driven IGNA Scout analysis and scoring",
                      "Maintain interview integrity and compliance",
                    ].map((text, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 20, color: "#9C27B0" }}
                        />
                        <Typography variant="caption">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>

            {/* Data Usage & Privacy Section */}
            <Box
              sx={{
                backgroundColor: "#E8F5E9",
                borderRadius: "8px",
                borderLeft: "6px solid #4CAF50", // Thicker green left border
                p: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <StorageIcon sx={{ color: "#4CAF50", fontSize: 25 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    Data Usage & Privacy
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5 }}
                  >
                    Your interview data and AI driven IGNA Scout generated scores will be:
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      "Stored securely and used only for recruitment purposes",
                      "Synchronized with Zoho Recruit for hiring decisions",
                      "Accessible only to authorized HR personnel",
                      "Retained according to company data retention policies",
                    ].map((text, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 20, color: "#4CAF50" }}
                        />
                        <Typography variant="caption">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>

            {/* Important Information Section */}
            <Box
              sx={{
                backgroundColor: "#FFF3E0",
                borderRadius: "8px",
                borderLeft: "6px solid #FF9800", // Thicker orange left border
                p: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <WarningAmberIcon sx={{ color: "#FF9800", fontSize: 28 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    Important Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    IGNA Scout is an assessment tool only. All final hiring
                    decisions, ratings, and approvals are made by human
                    recruiters in Zoho Recruit. AI driven IGNA Scout scores are provided as
                    reference data to support the hiring team's evaluation
                    process.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "#E0E0E0", my: 3 }} />

            {/* Consent Checkbox */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    sx={{
                      color: "#666",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and understood the above notices. I consent to
                    the AI driven IGNA Scout monitoring, recording, and data usage as described. I
                    understand that this is an assessment tool and all hiring
                    decisions are made by human recruiters.
                  </Typography>
                }
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="large"
                sx={{
                  textTransform: "none",
                  color: "#666",
                  borderColor: "#ddd",
                  "&:hover": { borderColor: "#bbb" },
                }}
              >
                Decline
              </Button>

              <Button
                onClick={handleProceed}
                variant="contained"
                size="large"
                disabled={!isChecked || loading}
                sx={{
                  textTransform: "none",
                  backgroundColor: "primary.main",
                  position: "relative",
                }}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Loading..." : "I Agree - Proceed to Device Check"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CandidateDisclaimer;
